import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import Layout from "@/components/layout/Layout";
import ShareEventButton from "@/components/ShareEventButton";
import { getEventThumb } from "@/lib/eventImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  Calendar, MapPin, Clock, Users, Ticket, Share2, 
  ArrowLeft, CheckCircle, DollarSign, ShoppingCart
} from "lucide-react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const EVENT_IMAGE = "https://images.unsplash.com/photo-1614554768702-1b026f460de5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxwcm9zdGF0ZSUyMGNhbmNlciUyMGF3YXJlbmVzcyUyMGJsdWUlMjByaWJib24lMjBldmVudCUyMGNoYXJpdHklMjBydW58ZW58MHx8fHwxNzc0MDMzMDY3fDA&ixlib=rb-4.1.0&q=85";

export default function EventDetailPage() {
  const { eventId } = useParams();
  const { user } = useAuth();
  const isAdmin = ["super_admin", "master_admin", "developer", "admin"].includes(user?.role);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [attendeeInfo, setAttendeeInfo] = useState({
    name: "",
    email: "",
    phone: ""
  });
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    fetchEvent();
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}`);
      setEvent(response.data);
      if (response.data.ticket_types?.length > 0) {
        setSelectedTicket(response.data.ticket_types[0].ticket_type_id);
      }
    } catch (error) {
      console.error("Error fetching event:", error);
      toast.error("Event not found");
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!attendeeInfo.name || !attendeeInfo.email) {
      toast.error("Please fill in your name and email");
      return;
    }

    setPurchasing(true);
    try {
      const response = await axios.post(`${API}/tickets/purchase`, {
        event_id: eventId,
        ticket_type_id: selectedTicket,
        quantity,
        attendee_name: attendeeInfo.name,
        attendee_email: attendeeInfo.email,
        attendee_phone: attendeeInfo.phone
      }, {
        headers: { origin: window.location.origin }
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else if (response.data.success) {
        // Free tickets — go to the same success page so user can download/print/save immediately
        toast.success("Tickets reserved! Loading your tickets...");
        const orderId = response.data.order_id;
        setTicketDialogOpen(false);
        window.location.href = `/tickets/success?order_id=${orderId}`;
      }
    } catch (error) {
      console.error("Error purchasing tickets:", error);
      toast.error(error.response?.data?.detail || "Failed to purchase tickets");
    } finally {
      setPurchasing(false);
    }
  };

  const handleAddToCart = async () => {
    const ticketType = event.ticket_types?.find(t => t.ticket_type_id === selectedTicket);
    if (!ticketType) { toast.error("Select a ticket type"); return; }
    try {
      await axios.post(`${API}/cart/add`, {
        item_type: "ticket",
        event_id: eventId,
        event_title: event.title,
        ticket_type_id: selectedTicket,
        ticket_type_name: ticketType.name,
        quantity,
        price: ticketType.price || 0,
        description: `${event.title} - ${ticketType.name}`
      }, { withCredentials: true });
      toast.success("Added to cart!");
    } catch (err) {
      if (err.response?.status === 401) toast.error("Please log in to add to cart");
      else toast.error("Failed to add to cart");
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      await navigator.share({
        title: event.title,
        text: event.description,
        url: window.location.href
      });
    } else {
      try { await navigator.clipboard.writeText(window.location.href); toast.success("Link copied to clipboard!"); }
      catch { toast.error("Clipboard unavailable — long-press the URL to copy"); }
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container-custom py-16">
          <div className="animate-pulse space-y-8">
            <div className="h-64 bg-slate-200 rounded-lg"></div>
            <div className="h-8 bg-slate-200 rounded w-1/2"></div>
            <div className="h-4 bg-slate-200 rounded w-1/4"></div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!event) {
    return (
      <Layout>
        <div className="container-custom py-16 text-center">
          <h1 className="text-2xl font-bold text-slate-700">Event Not Found</h1>
          <Link to="/events" className="text-awareness-blue mt-4 inline-block">
            Back to Events
          </Link>
        </div>
      </Layout>
    );
  }

  const selectedTicketType = event.ticket_types?.find(t => t.ticket_type_id === selectedTicket);

  return (
    <Layout>
      {/* Hero Image */}
      <div className="relative h-[40vh] md:h-[50vh]" data-testid="event-hero">
        <img 
          src={getEventThumb(event, EVENT_IMAGE)}
          alt={event.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="container-custom">
            <Link to="/events" className="inline-flex items-center text-white/80 hover:text-white mb-4" data-testid="back-to-events">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Events
            </Link>
            <span className="inline-block bg-awareness-blue text-deep-blue text-sm font-semibold px-4 py-1 rounded-full mb-4">
              {event.category}
            </span>
            <h1 className="text-3xl md:text-5xl font-bold text-white font-heading">{event.title}</h1>
          </div>
        </div>
      </div>

      <div className="container-custom py-12">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Event Details */}
            <div className="flex flex-wrap gap-6" data-testid="event-details">
              <div className="flex items-center gap-3 text-slate-700">
                <div className="w-12 h-12 bg-awareness-blue/10 rounded-lg flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-awareness-blue" />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Date</div>
                  <div className="font-semibold">{format(new Date(event.date), "MMMM d, yyyy")}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 text-slate-700">
                <div className="w-12 h-12 bg-awareness-blue/10 rounded-lg flex items-center justify-center">
                  <Clock className="w-6 h-6 text-awareness-blue" />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Time</div>
                  <div className="font-semibold">{format(new Date(event.date), "h:mm a")}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 text-slate-700">
                <div className="w-12 h-12 bg-awareness-blue/10 rounded-lg flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-awareness-blue" />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Location</div>
                  <div className="font-semibold">{event.location}</div>
                </div>
              </div>
            </div>

            {/* Description */}
            <div data-testid="event-description">
              <h2 className="text-heading-3 text-deep-blue mb-4">About This Event</h2>
              <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">{event.description}</p>
            </div>

            {/* Address */}
            {event.address && (
              <div data-testid="event-address">
                <h2 className="text-heading-3 text-deep-blue mb-4">Venue</h2>
                <p className="text-slate-600">{event.address}</p>
              </div>
            )}

            {/* Organizer */}
            {event.organizer && (
              <div data-testid="event-organizer">
                <h2 className="text-heading-3 text-deep-blue mb-4">Organizer</h2>
                <p className="text-slate-600">{event.organizer}</p>
              </div>
            )}

            {/* FAQ */}
            {event.faq && event.faq.length > 0 && (
              <div data-testid="event-faq">
                <h2 className="text-heading-3 text-deep-blue mb-4">Frequently Asked Questions</h2>
                <div className="space-y-4">
                  {event.faq.map((item, index) => (
                    <div key={index} className="bg-slate-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-deep-blue mb-2">{item.question}</h4>
                      <p className="text-slate-600 text-sm">{item.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Tickets */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-slate-200 rounded-lg p-6 sticky top-24" data-testid="ticket-sidebar">
              <h3 className="text-heading-3 text-deep-blue mb-4">Get Tickets</h3>
              
              {event.ticket_types && event.ticket_types.length > 0 ? (
                <div className="space-y-4">
                  {event.ticket_types.map(ticket => {
                    const available = ticket.quantity - (ticket.sold || 0);
                    return (
                      <div 
                        key={ticket.ticket_type_id}
                        className={`border rounded-lg p-4 cursor-pointer transition-all ${
                          selectedTicket === ticket.ticket_type_id 
                            ? 'border-awareness-blue bg-awareness-blue/5' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        onClick={() => setSelectedTicket(ticket.ticket_type_id)}
                        data-testid={`ticket-type-${ticket.ticket_type_id}`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-semibold text-deep-blue">{ticket.name}</h4>
                            {ticket.description && (
                              <p className="text-sm text-slate-500 mt-1">{ticket.description}</p>
                            )}
                            {isAdmin && (
                              <p className="text-sm text-slate-500 mt-1" data-testid={`ticket-availability-${ticket.ticket_type_id}`}>
                                Admin view: {available} of {ticket.quantity} available
                              </p>
                            )}
                            {!isAdmin && available <= 0 && (
                              <p className="text-sm text-red-600 mt-1">Sold out</p>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-xl font-bold text-deep-blue">
                              {ticket.price === 0 ? 'Free' : `$${ticket.price}`}
                            </div>
                            {ticket.early_bird && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                Early Bird
                              </span>
                            )}
                            {ticket.vip && (
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                                VIP
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  <div className="flex gap-2">
                    <Dialog open={ticketDialogOpen} onOpenChange={setTicketDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="flex-1 btn-primary" data-testid="buy-tickets-btn">
                          <Ticket className="w-4 h-4 mr-2" />
                          Get Tickets
                        </Button>
                      </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>Purchase Tickets</DialogTitle>
                        <DialogDescription>
                          Enter your details to purchase tickets for {event.title}
                        </DialogDescription>
                      </DialogHeader>
                      
                      <div className="space-y-4 py-4">
                        <div>
                          <Label htmlFor="ticket-type">Ticket Type</Label>
                          <Select value={selectedTicket} onValueChange={setSelectedTicket}>
                            <SelectTrigger data-testid="ticket-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {event.ticket_types.map(t => (
                                <SelectItem key={t.ticket_type_id} value={t.ticket_type_id}>
                                  {t.name} - {t.price === 0 ? 'Free' : `$${t.price}`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label htmlFor="quantity">Quantity</Label>
                          <Select value={quantity.toString()} onValueChange={(v) => setQuantity(parseInt(v))}>
                            <SelectTrigger data-testid="quantity-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                                <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label htmlFor="name">Full Name *</Label>
                          <Input 
                            id="name"
                            value={attendeeInfo.name}
                            onChange={(e) => setAttendeeInfo({...attendeeInfo, name: e.target.value})}
                            placeholder="John Doe"
                            data-testid="attendee-name"
                          />
                        </div>

                        <div>
                          <Label htmlFor="email">Email *</Label>
                          <Input 
                            id="email"
                            type="email"
                            value={attendeeInfo.email}
                            onChange={(e) => setAttendeeInfo({...attendeeInfo, email: e.target.value})}
                            placeholder="john@example.com"
                            data-testid="attendee-email"
                          />
                        </div>

                        <div>
                          <Label htmlFor="phone">Phone (Optional)</Label>
                          <Input 
                            id="phone"
                            value={attendeeInfo.phone}
                            onChange={(e) => setAttendeeInfo({...attendeeInfo, phone: e.target.value})}
                            placeholder="(555) 123-4567"
                            data-testid="attendee-phone"
                          />
                        </div>

                        {selectedTicketType && (
                          <div className="bg-slate-50 p-4 rounded-lg">
                            <div className="flex justify-between text-sm">
                              <span>{selectedTicketType.name} x {quantity}</span>
                              <span>
                                {selectedTicketType.price === 0 
                                  ? 'Free' 
                                  : `$${(selectedTicketType.price * quantity).toFixed(2)}`}
                              </span>
                            </div>
                            <div className="flex justify-between font-bold text-deep-blue mt-2 pt-2 border-t">
                              <span>Total</span>
                              <span>
                                {selectedTicketType.price === 0 
                                  ? 'Free' 
                                  : `$${(selectedTicketType.price * quantity).toFixed(2)}`}
                              </span>
                            </div>
                          </div>
                        )}

                        <Button 
                          onClick={handlePurchase} 
                          className="w-full btn-primary"
                          disabled={purchasing}
                          data-testid="confirm-purchase-btn"
                        >
                          {purchasing
                            ? "Processing..."
                            : selectedTicketType?.price === 0
                              ? `Reserve ${quantity > 1 ? `${quantity} Free Tickets` : "Free Ticket"} (No Payment Required)`
                              : `Pay $${((selectedTicketType?.price || 0) * quantity).toFixed(2)} & Get Tickets`}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                    <Button onClick={handleAddToCart} variant="outline" className="shrink-0" data-testid="add-to-cart-btn">
                      <ShoppingCart className="w-4 h-4 mr-2" /> Add to Cart
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">No tickets available</p>
              )}

              <div className="mt-4 flex justify-center">
                <ShareEventButton
                  event={event}
                  size="default"
                  className="w-full sm:w-auto"
                  data-testid="share-event-btn"
                />
              </div>
              <div className="mt-3 text-center">
                <Link to="/tickets/retrieve" className="text-xs text-slate-500 hover:text-deep-blue underline" data-testid="retrieve-tickets-link">
                  Already bought tickets? Retrieve them by email
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
